import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { 
  ShieldCheck, 
  Search, 
  MessageSquareWarning, 
  Globe, 
  LifeBuoy, 
  FileCheck2, 
  Users, 
  ArrowRight, 
  CheckCircle2, 
  AlertTriangle, 
  Lock, 
  Eye, 
  BellRing, 
  Sparkles, 
  ChevronRight,
  Fingerprint,
  FileSpreadsheet,
  Building2,
  HelpCircle
} from 'lucide-react';
import { ScoreGauge } from '../components/common/ScoreGauge';
import { useStore } from '../store/useStore';

export const Home: React.FC = () => {
  const navigate = useNavigate();
  const { openAiModal, securityScore } = useStore();
  const [quickSearchInput, setQuickSearchInput] = useState('');

  const handleQuickCheck = (e: React.FormEvent) => {
    e.preventDefault();
    if (quickSearchInput.trim()) {
      navigate(`/check-identity?q=${encodeURIComponent(quickSearchInput.trim())}`);
    } else {
      navigate('/check-identity');
    }
  };

  return (
    <div className="space-y-24 pb-20">
      {/* HERO SECTION */}
      <section className="relative pt-12 pb-16 overflow-hidden">
        {/* Ambient background glow */}
        <div className="absolute top-1/4 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[700px] h-[450px] bg-emerald-500/10 blur-[130px] rounded-full pointer-events-none -z-10" />
        <div className="absolute top-1/3 right-10 w-[400px] h-[350px] bg-blue-500/10 blur-[120px] rounded-full pointer-events-none -z-10" />

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
            {/* Left Col: Headline & Pitch */}
            <div className="lg:col-span-7 space-y-6 text-center lg:text-left">
              {/* Australian Trust Badge */}
              <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-emerald-950/80 border border-emerald-800/80 text-xs font-semibold text-emerald-300">
                <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />
                <span>AUSTRALIA'S PERSONAL IDENTITY SECURITY CENTRE</span>
              </div>

              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold text-white tracking-tight leading-[1.15]">
                Is your identity <span className="text-transparent bg-clip-text bg-gradient-to-r from-emerald-400 via-teal-300 to-cyan-400">at risk?</span>
              </h1>

              <p className="text-lg sm:text-xl text-slate-300 max-w-2xl mx-auto lg:mx-0 font-normal leading-relaxed">
                Check your exposure, detect suspicious activity, and get clear, step-by-step guidance to protect yourself from identity fraud and Australian scams.
              </p>

              {/* Core Philosophy Pill */}
              <div className="flex flex-wrap items-center justify-center lg:justify-start gap-3 text-xs font-medium text-slate-400 pt-1">
                <span className="px-2.5 py-1 rounded bg-slate-900 border border-slate-800 text-slate-300">1. Detect Exposure</span>
                <span>→</span>
                <span className="px-2.5 py-1 rounded bg-slate-900 border border-slate-800 text-slate-300">2. Understand Risk</span>
                <span>→</span>
                <span className="px-2.5 py-1 rounded bg-slate-900 border border-slate-800 text-slate-300">3. Protect Documents</span>
                <span>→</span>
                <span className="px-2.5 py-1 rounded bg-slate-900 border border-slate-800 text-slate-300">4. Recover Step-by-Step</span>
              </div>

              {/* Fast Quick Search Form */}
              <form onSubmit={handleQuickCheck} className="pt-2 max-w-xl mx-auto lg:mx-0">
                <div className="flex flex-col sm:flex-row gap-2 bg-slate-900/90 p-2 rounded-2xl border border-slate-700/80 shadow-2xl">
                  <div className="relative flex-1">
                    <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                    <input
                      type="text"
                      value={quickSearchInput}
                      onChange={(e) => setQuickSearchInput(e.target.value)}
                      placeholder="Enter email, phone, or username to check..."
                      className="w-full pl-11 pr-4 py-3 bg-transparent text-white placeholder-slate-500 text-sm focus:outline-none"
                    />
                  </div>
                  <button
                    type="submit"
                    className="px-6 py-3 bg-emerald-600 hover:bg-emerald-500 text-white rounded-xl font-semibold text-sm shadow-lg shadow-emerald-950/40 transition-all flex items-center justify-center gap-2 flex-shrink-0"
                  >
                    <span>Check My Identity</span>
                    <ArrowRight className="w-4 h-4" />
                  </button>
                </div>
                <p className="text-xs text-slate-400 mt-2 flex items-center justify-center lg:justify-start gap-1.5">
                  <Lock className="w-3.5 h-3.5 text-emerald-400" />
                  <span>We never ask for passwords. 100% confidential and privacy-first.</span>
                </p>
              </form>

              {/* Secondary CTAs */}
              <div className="flex flex-wrap items-center justify-center lg:justify-start gap-3 pt-2">
                <Link
                  to="/scam-checker"
                  className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-slate-900 hover:bg-slate-800 border border-slate-700 text-slate-200 text-sm font-medium transition-colors"
                >
                  <MessageSquareWarning className="w-4 h-4 text-amber-400" />
                  <span>Check a Suspicious Scam</span>
                </Link>
                <Link
                  to="/website-checker"
                  className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-slate-900 hover:bg-slate-800 border border-slate-700 text-slate-200 text-sm font-medium transition-colors"
                >
                  <Globe className="w-4 h-4 text-blue-400" />
                  <span>Analyze a Website URL</span>
                </Link>
              </div>
            </div>

            {/* Right Col: Interactive Visual Identity Security Dashboard Preview */}
            <div className="lg:col-span-5">
              <div className="relative">
                {/* Decorative border frame */}
                <div className="absolute -inset-1 bg-gradient-to-r from-emerald-500/30 to-teal-500/20 rounded-3xl blur-xl -z-10" />

                <div className="bg-slate-900/95 border border-slate-800 rounded-2xl shadow-2xl p-6 space-y-6">
                  {/* Top Bar Preview */}
                  <div className="flex items-center justify-between border-b border-slate-800 pb-4">
                    <div className="flex items-center gap-2.5">
                      <div className="w-8 h-8 rounded-lg bg-emerald-600/20 border border-emerald-500/30 flex items-center justify-center">
                        <Fingerprint className="w-4 h-4 text-emerald-400" />
                      </div>
                      <div>
                        <h4 className="text-sm font-bold text-white">Identity Health Overview</h4>
                        <p className="text-[11px] text-slate-400">Live Continuous Telemetry</p>
                      </div>
                    </div>
                    <span className="px-2 py-0.5 rounded-full text-[10px] font-semibold bg-emerald-950 text-emerald-300 border border-emerald-800">
                      ACTIVE MONITORING
                    </span>
                  </div>

                  {/* Score & Gauge Preview */}
                  <div className="flex items-center gap-6 bg-slate-950/80 p-4 rounded-xl border border-slate-800">
                    <ScoreGauge score={securityScore.overallScore} size="sm" showIcon={false} />
                    <div className="space-y-1">
                      <div className="flex items-center gap-1.5">
                        <span className="w-2 h-2 rounded-full bg-emerald-400" />
                        <span className="text-xs font-semibold text-slate-200">Security Score: 82 / 100</span>
                      </div>
                      <p className="text-xs text-slate-400 leading-snug">
                        Your identity security is good. 2 documents monitored, no active loan inquiries.
                      </p>
                      <Link to="/security-score" className="text-xs font-semibold text-emerald-400 hover:text-emerald-300 inline-flex items-center gap-1 pt-1">
                        <span>Improve My Score</span>
                        <ChevronRight className="w-3 h-3" />
                      </Link>
                    </div>
                  </div>

                  {/* Live Mini Cards */}
                  <div className="space-y-2.5 text-xs">
                    {/* Breach Alert Pill */}
                    <div className="flex items-center justify-between p-3 rounded-xl bg-amber-950/30 border border-amber-800/40 text-amber-200">
                      <div className="flex items-center gap-2">
                        <AlertTriangle className="w-4 h-4 text-amber-400 flex-shrink-0" />
                        <span className="font-medium">1 Past Breach Exposure Detected</span>
                      </div>
                      <Link to="/alerts" className="text-[11px] font-bold text-amber-300 hover:underline">
                        Review Alert
                      </Link>
                    </div>

                    {/* ID Documents Pill */}
                    <div className="flex items-center justify-between p-3 rounded-xl bg-slate-950 border border-slate-800 text-slate-300">
                      <div className="flex items-center gap-2">
                        <CheckCircle2 className="w-4 h-4 text-emerald-400 flex-shrink-0" />
                        <span>NSW Driver Licence Card # Monitored</span>
                      </div>
                      <span className="text-[11px] text-emerald-400 font-mono">DVS Validated</span>
                    </div>

                    {/* Scam Protection Pill */}
                    <div className="flex items-center justify-between p-3 rounded-xl bg-slate-950 border border-slate-800 text-slate-300">
                      <div className="flex items-center gap-2">
                        <ShieldCheck className="w-4 h-4 text-blue-400 flex-shrink-0" />
                        <span>Credit Inquiries Shield Active (Equifax)</span>
                      </div>
                      <span className="text-[11px] text-slate-400">Zero Inquiries</span>
                    </div>
                  </div>

                  {/* CTA link to full dashboard */}
                  <Link
                    to="/dashboard"
                    className="w-full py-2.5 rounded-xl bg-slate-800 hover:bg-slate-700 text-slate-200 font-medium text-xs flex items-center justify-center gap-1.5 transition-colors"
                  >
                    <span>Open Live Personal Dashboard</span>
                    <ArrowRight className="w-3.5 h-3.5" />
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* TRUST & AUSTRALIAN REALITY SECTION (No fake badges) */}
      <section className="border-y border-slate-800/80 bg-slate-900/40 py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
            <div className="space-y-1.5">
              <div className="text-2xl sm:text-3xl font-extrabold text-white">Australian-Focused</div>
              <p className="text-xs text-slate-400">Designed around DVS, myGov, Medicare, ATO & state licences</p>
            </div>

            <div className="space-y-1.5">
              <div className="text-2xl sm:text-3xl font-extrabold text-emerald-400">Privacy-First</div>
              <p className="text-xs text-slate-400">Data minimisation. We never ask for or store your passwords</p>
            </div>

            <div className="space-y-1.5">
              <div className="text-2xl sm:text-3xl font-extrabold text-white">Actionable Steps</div>
              <p className="text-xs text-slate-400">Don't just get alerted. Get exact step-by-step remediation plans</p>
            </div>

            <div className="space-y-1.5">
              <div className="text-2xl sm:text-3xl font-extrabold text-teal-400">Official Standards</div>
              <p className="text-xs text-slate-400">Aligned with IDCARE, ACSC Cyber.gov.au & Scamwatch guidance</p>
            </div>
          </div>
        </div>
      </section>

      {/* 4-STAGE CORE PHILOSOPHY BREAKDOWN */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center max-w-3xl mx-auto space-y-4">
          <h2 className="text-3xl sm:text-4xl font-extrabold text-white">
            Know what's happening with your identity. <span className="text-emerald-400">Protect it before fraud happens.</span>
          </h2>
          <p className="text-base text-slate-300">
            We avoid fear-mongering and panic. Instead, we give every Australian clear, methodical control across four essential stages.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mt-12">
          {/* 1. Detect */}
          <div className="p-6 rounded-2xl bg-slate-900/80 border border-slate-800 hover:border-emerald-700/60 transition-colors space-y-4">
            <div className="w-10 h-10 rounded-xl bg-emerald-950 border border-emerald-800/80 flex items-center justify-center text-emerald-400 font-bold">
              1
            </div>
            <h3 className="text-lg font-bold text-white">Detect Exposure</h3>
            <p className="text-xs text-slate-400 leading-relaxed">
              Check whether your email, phone number, name, or identity documents have appeared in Australian or international corporate breaches.
            </p>
            <Link to="/check-identity" className="text-xs font-semibold text-emerald-400 hover:underline inline-flex items-center gap-1">
              Check exposure now <ChevronRight className="w-3 h-3" />
            </Link>
          </div>

          {/* 2. Understand */}
          <div className="p-6 rounded-2xl bg-slate-900/80 border border-slate-800 hover:border-blue-700/60 transition-colors space-y-4">
            <div className="w-10 h-10 rounded-xl bg-blue-950 border border-blue-800/80 flex items-center justify-center text-blue-400 font-bold">
              2
            </div>
            <h3 className="text-lg font-bold text-white">Understand Risk</h3>
            <p className="text-xs text-slate-400 leading-relaxed">
              Analyze suspicious SMS, toll messages, fake ATO emails, and risky websites with our AI Scam & Threat Classification Engine.
            </p>
            <Link to="/scam-checker" className="text-xs font-semibold text-blue-400 hover:underline inline-flex items-center gap-1">
              Analyze scam message <ChevronRight className="w-3 h-3" />
            </Link>
          </div>

          {/* 3. Protect */}
          <div className="p-6 rounded-2xl bg-slate-900/80 border border-slate-800 hover:border-teal-700/60 transition-colors space-y-4">
            <div className="w-10 h-10 rounded-xl bg-teal-950 border border-teal-800/80 flex items-center justify-center text-teal-400 font-bold">
              3
            </div>
            <h3 className="text-lg font-bold text-white">Protect Documents</h3>
            <p className="text-xs text-slate-400 leading-relaxed">
              Safeguard your Driver Licence Card Number, Medicare Card, and TFN. Apply secure digital watermarks before sharing ID copies for rent or hire.
            </p>
            <Link to="/id-watermark" className="text-xs font-semibold text-teal-400 hover:underline inline-flex items-center gap-1">
              Generate ID watermark <ChevronRight className="w-3 h-3" />
            </Link>
          </div>

          {/* 4. Recover */}
          <div className="p-6 rounded-2xl bg-slate-900/80 border border-slate-800 hover:border-rose-700/60 transition-colors space-y-4">
            <div className="w-10 h-10 rounded-xl bg-rose-950 border border-rose-800/80 flex items-center justify-center text-rose-400 font-bold">
              4
            </div>
            <h3 className="text-lg font-bold text-white">Recover Step-by-Step</h3>
            <p className="text-xs text-slate-400 leading-relaxed">
              If an incident occurs, follow an interactive 8-step recovery roadmap connecting you with IDCARE, credit bans, and state road registries.
            </p>
            <Link to="/identity-theft" className="text-xs font-semibold text-rose-400 hover:underline inline-flex items-center gap-1">
              Start emergency response <ChevronRight className="w-3 h-3" />
            </Link>
          </div>
        </div>
      </section>

      {/* LIVE INTERACTIVE TOOLS HIGHLIGHT */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="bg-gradient-to-br from-slate-900 via-slate-900 to-navy-950 border border-slate-800 rounded-3xl p-8 sm:p-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="space-y-6">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-amber-950 text-amber-300 text-xs font-semibold border border-amber-800">
                <MessageSquareWarning className="w-3.5 h-3.5" />
                <span>AI Scam & Phishing Detection</span>
              </div>
              <h2 className="text-3xl sm:text-4xl font-extrabold text-white">
                Received a strange text or email? <span className="text-amber-400">Check it in seconds.</span>
              </h2>
              <p className="text-slate-300 text-sm leading-relaxed">
                Scammers target millions of Australians daily with fake toll notices (Linkt), Australia Post parcel updates, myGov tax refunds, and PayID marketplace tricks. Paste any message into our analyzer to receive an instant risk rating and safe response advice.
              </p>
              <div className="flex flex-wrap gap-4 pt-2">
                <Link
                  to="/scam-checker"
                  className="px-5 py-2.5 rounded-xl bg-amber-500 hover:bg-amber-400 text-slate-950 font-bold text-sm transition-colors flex items-center gap-2"
                >
                  <MessageSquareWarning className="w-4 h-4" />
                  <span>Open Scam Checker</span>
                </Link>
                <button
                  onClick={openAiModal}
                  className="px-5 py-2.5 rounded-xl bg-slate-800 hover:bg-slate-700 text-white font-medium text-sm transition-colors flex items-center gap-2"
                >
                  <Sparkles className="w-4 h-4 text-emerald-400" />
                  <span>Ask AI Assistant</span>
                </button>
              </div>
            </div>

            {/* Visual Example Card */}
            <div className="bg-slate-950/90 border border-slate-800 p-6 rounded-2xl shadow-xl space-y-4 font-mono text-xs">
              <div className="flex items-center justify-between border-b border-slate-800 pb-3">
                <span className="text-slate-400">SAMPLE ANALYSIS RESULT</span>
                <span className="px-2 py-0.5 rounded bg-rose-950 text-rose-300 border border-rose-800 font-bold">
                  HIGH RISK (96%)
                </span>
              </div>
              <div className="bg-slate-900 p-3 rounded-lg text-slate-300 border border-slate-800 italic">
                "AusPost: Your package AU-88219 is on hold due to $2.35 unpaid fee. Update within 24hrs at https://auspost-redelivery.net"
              </div>
              <div className="space-y-1.5 text-slate-400">
                <p className="text-rose-400 font-bold font-sans">Why is this a scam?</p>
                <p>• Impersonation of Australia Post using fraudulent non-official domain (.net).</p>
                <p>• Artificial urgency (24 hours) designed to provoke hasty card payment.</p>
                <p>• Solicits credit card details for a tiny handling fee.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* PRICING PREVIEW BANNER */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center space-y-8">
        <div className="max-w-2xl mx-auto space-y-3">
          <h2 className="text-3xl font-extrabold text-white">Simple, Transparent Australian Plans</h2>
          <p className="text-slate-400 text-sm">
            Start for free with on-demand breach and scam checks, or activate 24/7 continuous monitoring for you and your family.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 text-left">
          {/* Free */}
          <div className="p-6 rounded-2xl bg-slate-900 border border-slate-800 space-y-4">
            <h3 className="text-xl font-bold text-white">Free</h3>
            <div className="text-3xl font-extrabold text-white">$0 <span className="text-xs text-slate-400 font-normal">forever</span></div>
            <p className="text-xs text-slate-400">Essential breach search, scam checks, and basic recovery guides.</p>
            <ul className="text-xs space-y-2 text-slate-300 pt-2">
              <li className="flex items-center gap-2"><CheckCircle2 className="w-3.5 h-3.5 text-emerald-400" /> Identity exposure search</li>
              <li className="flex items-center gap-2"><CheckCircle2 className="w-3.5 h-3.5 text-emerald-400" /> AI Scam message analyzer</li>
              <li className="flex items-center gap-2"><CheckCircle2 className="w-3.5 h-3.5 text-emerald-400" /> ID Watermark generator</li>
            </ul>
            <Link to="/register" className="block w-full py-2.5 text-center text-xs font-semibold bg-slate-800 hover:bg-slate-700 text-white rounded-xl transition-colors">
              Get Started Free
            </Link>
          </div>

          {/* Personal */}
          <div className="p-6 rounded-2xl bg-slate-900 border-2 border-emerald-500 space-y-4 relative shadow-xl shadow-emerald-950/30">
            <div className="absolute -top-3 right-4 px-2.5 py-0.5 rounded-full bg-emerald-500 text-slate-950 text-[10px] font-extrabold uppercase">
              Most Popular
            </div>
            <h3 className="text-xl font-bold text-white">Personal</h3>
            <div className="text-3xl font-extrabold text-white">$9.95 <span className="text-xs text-slate-400 font-normal">/ month</span></div>
            <p className="text-xs text-slate-400">24/7 continuous identity monitoring & fast breach alerts.</p>
            <ul className="text-xs space-y-2 text-slate-300 pt-2">
              <li className="flex items-center gap-2"><CheckCircle2 className="w-3.5 h-3.5 text-emerald-400" /> Continuous dark web surveillance</li>
              <li className="flex items-center gap-2"><CheckCircle2 className="w-3.5 h-3.5 text-emerald-400" /> Driver licence & passport tracking</li>
              <li className="flex items-center gap-2"><CheckCircle2 className="w-3.5 h-3.5 text-emerald-400" /> 8-Step Emergency Recovery Wizard</li>
            </ul>
            <Link to="/pricing" className="block w-full py-2.5 text-center text-xs font-bold bg-emerald-600 hover:bg-emerald-500 text-white rounded-xl shadow-md transition-colors">
              Start Personal Protection
            </Link>
          </div>

          {/* Family */}
          <div className="p-6 rounded-2xl bg-slate-900 border border-slate-800 space-y-4">
            <h3 className="text-xl font-bold text-white">Family</h3>
            <div className="text-3xl font-extrabold text-white">$19.95 <span className="text-xs text-slate-400 font-normal">/ month</span></div>
            <p className="text-xs text-slate-400">Total protection for up to 5 family members under one roof.</p>
            <ul className="text-xs space-y-2 text-slate-300 pt-2">
              <li className="flex items-center gap-2"><CheckCircle2 className="w-3.5 h-3.5 text-emerald-400" /> Everything in Personal</li>
              <li className="flex items-center gap-2"><CheckCircle2 className="w-3.5 h-3.5 text-emerald-400" /> 5 Family member profiles</li>
              <li className="flex items-center gap-2"><CheckCircle2 className="w-3.5 h-3.5 text-emerald-400" /> Elderly parent scam shield</li>
            </ul>
            <Link to="/pricing" className="block w-full py-2.5 text-center text-xs font-semibold bg-slate-800 hover:bg-slate-700 text-white rounded-xl transition-colors">
              Start Family Protection
            </Link>
          </div>
        </div>
      </section>

      {/* EMERGENCY CTA BAR */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="p-8 rounded-2xl bg-gradient-to-r from-rose-950/80 via-slate-900 to-slate-900 border border-rose-800/60 flex flex-col md:flex-row items-center justify-between gap-6">
          <div className="space-y-2 text-center md:text-left">
            <h3 className="text-xl font-bold text-white flex items-center justify-center md:justify-start gap-2">
              <LifeBuoy className="w-5 h-5 text-rose-400" />
              <span>Think someone has stolen your identity?</span>
            </h3>
            <p className="text-xs text-slate-300 max-w-xl">
              Don't panic. Our Emergency Response Centre provides a customized Australian recovery roadmap and connects you with IDCARE, Equifax, and state authorities.
            </p>
          </div>
          <Link
            to="/identity-theft"
            className="px-6 py-3 rounded-xl bg-rose-600 hover:bg-rose-500 text-white font-bold text-sm shadow-lg shadow-rose-950/40 transition-colors flex-shrink-0"
          >
            Start Emergency Response
          </Link>
        </div>
      </section>
    </div>
  );
};
